import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { useLantern, useSession } from "../hooks";
import { navigate } from "../router";
import { rank, type Entry } from "../switcher";
import { usePeopleSearch } from "../peopleSearch";
import type { Channel } from "../sync/types";
import { Avatar, useDialog } from "./ui";
import { adminGroups, settingsGroups } from "./SettingsNav";
import { t, tc, tx } from "../i18n";

type Item = Entry & { kind: "channel" | "person" | "join" | "page" | "search"; icon?: string; go: () => Promise<void> | void; id?: string };

// QuickSwitcher jumps to a channel, a DM, a person (opening a DM with them),
// a public channel to join, or a page, by typing a few letters.
export function QuickSwitcher({ onClose }: { onClose: () => void }) {
  const session = useSession();
  const state = useLantern();
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const [joinable, setJoinable] = useState<Channel[]>([]);
  const [error, setError] = useState("");
  const listID = useId();
  const box = useRef<HTMLInputElement>(null);
  const dialog = useRef<HTMLDivElement>(null);
  useDialog(dialog, onClose);
  const me = state.me;
  const found = usePeopleSearch(q);

  useEffect(() => {
    session.api.browse().then(
      (r) => setJoinable(r.channels.filter((c) => !c.membership && c.kind === "public" && !c.archived_at)),
      () => {},
    );
  }, [session]);

  const go = (path: string) => () => navigate(path);
  const items: Item[] = [];
  const dmWith = new Map<string, string>();
  for (const cs of state.channels.values()) {
    const c = cs.channel;
    const m = c.membership;
    const dm = c.kind === "dm" || c.kind === "group_dm";
    const others = (session.dmMembers.get(c.id) ?? []).filter((x) => x !== me?.id);
    if (c.kind === "dm" && others.length === 1) dmWith.set(others[0], c.id);
    items.push({
      key: c.id,
      id: dm && others.length === 1 ? others[0] : undefined,
      kind: "channel",
      label: session.channelName(c.id),
      also: dm ? others.map((o) => "@" + (session.directory.get(o)?.handle ?? "")).join(" ") : c.topic,
      icon: c.kind === "private" ? "🔒" : dm ? undefined : "#",
      unread: (dm ? m?.unread_count : m?.mention_count) || (m?.unread_count ? 0.5 : 0),
      rank: c.archived_at ? 9 : 0,
      go: go(`/c/${c.id}`),
    });
  }
  // In a large workspace the server finds the people who match.
  for (const p of found ?? session.directory.values()) {
    if (p.id === me?.id || p.status !== "active" || p.kind === "system" || dmWith.has(p.id)) continue;
    items.push({
      key: "p" + p.id,
      id: p.id,
      kind: "person",
      label: p.display_name || p.handle,
      also: "@" + p.handle,
      rank: p.kind === "agent" ? 3 : 2,
      go: async () => {
        const { channel } = await session.api.openDM([p.id]);
        await session.loadDMMembers();
        navigate(`/c/${channel.id}`);
      },
    });
  }
  for (const c of joinable) {
    items.push({
      key: "j" + c.id,
      kind: "join",
      label: "#" + c.name,
      also: c.topic,
      icon: "#",
      rank: 4,
      go: async () => {
        await session.api.joinChannel(c.id);
        navigate(`/c/${c.id}`);
      },
    });
  }
  const pages: [string, string, boolean?][] = [
    [t("Inbox"), "/inbox"],
    [t("Threads"), "/threads"],
    [t("Huddles"), "/huddles"],
    [t("Files"), "/files"],
    [t("Saved for later"), "/saved"],
    [t("Scheduled messages"), "/scheduled"],
    [t("People"), "/people"],
    [t("Search"), "/search"],
    [t("Tasks"), "/tasks"],
    [t("Settings"), "/settings/preferences"],
    [t("Help"), "/help"],
    [t("Keyboard shortcuts"), "/help#shortcuts"],
    [t("Fleet"), "/fleet", true],
    [t("Workflows"), "/workflows", true],
    [t("Administration"), "/admin/workspace", true],
  ];
  for (const [label, path, admin] of pages) {
    if (admin && me?.role !== "admin") continue;
    items.push({ key: path, kind: "page", label, icon: "→", rank: 5, go: go(path) });
  }
  // Each page of Settings and of Administration, found also by what is on
  // it: "passkeys" finds Security.
  const places: [string, string, ReturnType<typeof settingsGroups>][] = [[t("Settings"), "/settings/", settingsGroups()]];
  if (me?.role === "admin") places.push([t("Administration"), "/admin/", adminGroups()]);
  for (const [where, base, groups] of places)
    for (const [, group] of groups)
      for (const p of group) {
        if (p.to) continue;
        items.push({
          key: base + p.key,
          kind: "page",
          label: t("{place}: {page}", { place: where, page: p.label }),
          also: [p.label, ...(p.keywords ?? [])].join(" "),
          icon: "→",
          rank: 5,
          go: go(base + p.key),
        });
      }

  // With nothing typed: what is waiting first, then channels.
  // Anything typed can also be searched for in messages: last, so Enter
  // still jumps to the best match, or searches when nothing matches.
  const search: Item[] = q.trim()
    ? [{ key: "search", kind: "search", label: t("Search messages for “{q}”", { q: q.trim() }), icon: "⌕", go: go(`/search?q=${encodeURIComponent(q.trim())}`) }]
    : [];
  const shown = q.trim()
    ? [...rank(items, q, 9), ...search]
    : rank(
        items.filter((i) => i.kind === "channel" && !i.rank),
        "",
        8,
      );
  const at = Math.min(active, Math.max(shown.length - 1, 0));

  async function choose(i: Item | undefined) {
    if (!i) return;
    setError("");
    try {
      await i.go();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }
  function onKey(e: KeyboardEvent) {
    if (e.key === "ArrowDown" || e.key === "ArrowUp" || (e.key === "Tab" && shown.length)) {
      e.preventDefault();
      const d = e.key === "ArrowUp" || (e.key === "Tab" && e.shiftKey) ? -1 : 1;
      setActive((at + d + shown.length) % Math.max(shown.length, 1));
    } else if (e.key === "Enter" && !e.nativeEvent.isComposing) {
      e.preventDefault();
      void choose(shown[at]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
  }

  return createPortal(
    <div className="switcher-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={dialog} className="switcher" role="dialog" aria-modal="true" aria-label={t("Jump to")}>
        <input
          ref={box}
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setActive(0);
          }}
          onKeyDown={onKey}
          placeholder={t("Search messages, or jump to a channel, person or page…")}
          aria-label={t("Jump to")}
          role="combobox"
          aria-expanded="true"
          aria-controls={listID}
          aria-activedescendant={shown[at] ? `${listID}-${at}` : undefined}
          autoFocus
          autoComplete="off"
          spellCheck={false}
        />
        {error && <p className="error">{error}</p>}
        {!q.trim() && shown.length > 0 && (
          <p className="switcher-heading" id={`${listID}-heading`}>
            {t("Channels and conversations")}
          </p>
        )}
        <ul id={listID} role="listbox" {...(!q.trim() && shown.length > 0 ? { "aria-labelledby": `${listID}-heading` } : { "aria-label": t("Matches") })}>
          {shown.map((i, n) => (
            <li
              key={i.key}
              id={`${listID}-${n}`}
              role="option"
              aria-selected={n === at}
              className={n === at ? "active" : ""}
              onMouseMove={() => setActive(n)}
              onClick={() => void choose(i)}
            >
              <span className="switcher-icon">{i.id ? <Avatar id={i.id} name={i.label} size={22} /> : i.icon}</span>
              <span className="switcher-label">{i.label}</span>
              {i.also && i.kind !== "page" && <span className="switcher-also">{i.also}</span>}
              {i.kind === "join" && <span className="switcher-tag">{t("Join")}</span>}
              {i.kind === "person" && <span className="switcher-tag">{tc("action", "Message")}</span>}
              {i.kind === "page" && <span className="switcher-tag">{t("Page")}</span>}
              {i.kind === "search" && <span className="switcher-tag">{t("Search")}</span>}
              {(i.unread ?? 0) >= 1 && <span className="badge">{i.unread}</span>}
            </li>
          ))}
        </ul>
        {q.trim() && shown.length === 0 && <p className="switcher-none">{t("Nothing matches “{q}”.", { q: q.trim() })}</p>}
        <p className="switcher-hint">
          {tx("{up}{down} to choose · {enter} to go · {esc} to close", { up: <kbd>↑</kbd>, down: <kbd>↓</kbd>, enter: <kbd>Enter</kbd>, esc: <kbd>Esc</kbd> })}
        </p>
      </div>
    </div>,
    document.body,
  );
}
